import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Newspaper, FileSearch, Briefcase, Handshake,
  TrendingUp, ShieldCheck, Database, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import ArticleCard from "@/components/ArticleCard";

const LAYERS = [
  { icon: Newspaper, title: "Content", desc: "Technology and business journalism engineered to attract organic and referral traffic.", to: "/articles", tag: "Media" },
  { icon: FileSearch, title: "Research", desc: "Gated reports, surveys and white papers that generate qualified B2B leads.", to: "/reports", tag: "Intelligence" },
  { icon: Briefcase, title: "Business Solutions", desc: "Commercial landing pages built to drive quote requests and buyer inquiries.", to: "/solutions", tag: "Demand" },
  { icon: Handshake, title: "Partnerships", desc: "Sponsored content, lead-gen campaigns, advertising and performance marketing.", to: "/partnerships", tag: "Growth" },
];

const STAT_ICONS = { articles: Newspaper, reports: FileSearch, leads: TrendingUp, categories: Database };

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/articles", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-white">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-electric/20 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="lg:col-span-7">
              <span className="overline-label text-electric">B2B Technology · Research · Growth</span>
              <h1 className="mt-5 font-display font-extrabold leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                Connecting Technology,<br /> Business &amp; <span className="text-electric">Growth</span>
              </h1>
              <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
                Lead ITES delivers technology insights, industry research, business intelligence and
                qualified opportunities that help modern businesses make smarter decisions.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Button asChild className="btn-electric rounded-full px-6 h-11" data-testid="cta-explore-insights">
                  <Link to="/articles">Explore Insights <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full px-6 h-11 bg-transparent border-white/30 text-white hover:bg-white hover:text-navy" data-testid="cta-find-solutions">
                  <Link to="/solutions">Find Business Solutions</Link>
                </Button>
                <Button asChild variant="ghost" className="rounded-full px-6 h-11 text-white hover:bg-white/10" data-testid="cta-partner">
                  <Link to="/partnerships">Partner With Us <ArrowUpRight className="h-4 w-4" /></Link>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-white/15">
                <img src="https://images.unsplash.com/photo-1644088379091-d574269d422f"
                  alt="Data intelligence" className="w-full h-[360px] object-cover" />
                <div className="absolute bottom-4 left-4 right-4 glass rounded-xl p-4 border border-white/30">
                  <p className="overline-label text-navy">Insights, Intelligence &amp; Opportunities</p>
                  <p className="text-navy font-display font-bold mt-1">For modern businesses</p>
                </div>
              </div>
            </motion.div>
          </div>

          {stats && (
            <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden border border-white/10">
              {Object.entries(stats).map(([k, v]) => {
                const Icon = STAT_ICONS[k] || Database;
                return (
                  <div key={k} className="bg-navy p-6" data-testid={`stat-${k}`}>
                    <Icon className="h-5 w-5 text-electric" />
                    <p className="mt-3 font-display text-3xl font-extrabold">{v}+</p>
                    <p className="text-white/60 text-sm capitalize">{k}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* BUSINESS LAYERS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20">
        <div className="max-w-2xl">
          <span className="overline-label text-electric">The Lead ITES Model</span>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl font-extrabold text-navy">
            Four layers that turn attention into opportunity
          </h2>
          <p className="mt-4 text-muted-foreground">
            A connected platform spanning media, research, commercial demand and strategic partnerships.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {LAYERS.map((l, i) => (
            <motion.div key={l.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }}>
              <Link to={l.to} className="group card-lift block h-full bg-white border border-border rounded-xl p-7"
                data-testid={`layer-${l.title.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="h-11 w-11 rounded-lg bg-navy flex items-center justify-center">
                  <l.icon className="h-5 w-5 text-electric" />
                </div>
                <span className="overline-label text-muted-foreground block mt-5">{l.tag}</span>
                <h3 className="mt-1 font-display text-xl font-bold text-navy group-hover:text-electric transition-colors">{l.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{l.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-electric">
                  Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED ARTICLES */}
      <section className="bg-secondary/60 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className="overline-label text-electric">Latest Insights</span>
              <h2 className="mt-3 font-display text-3xl lg:text-4xl font-extrabold text-navy">Featured coverage</h2>
            </div>
            <Button asChild variant="outline" className="rounded-full" data-testid="view-all-articles">
              <Link to="/articles">View all articles <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20">
        <div className="flex flex-col lg:flex-row gap-10 lg:items-center">
          <div className="lg:w-1/3">
            <span className="overline-label text-electric">Content Platform</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold text-navy">Coverage across the enterprise stack</h2>
            <p className="mt-4 text-muted-foreground">From AI and cybersecurity to sales and digital transformation.</p>
          </div>
          <div className="lg:w-2/3 flex flex-wrap gap-3">
            {CATEGORIES.map((c) => (
              <Link key={c} to={`/articles?category=${encodeURIComponent(c)}`}
                className="px-5 py-2.5 rounded-full border border-border bg-white text-sm font-medium text-navy hover:bg-navy hover:text-white hover:border-navy transition-colors"
                data-testid={`home-category-${c.replace(/\s|&/g, "-").toLowerCase()}`}>
                {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP CTA */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-8">
        <div className="relative overflow-hidden rounded-2xl bg-navy text-white p-10 lg:p-16">
          <div className="absolute inset-0 grain opacity-40" />
          <div className="absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-electric/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex gap-2 mb-4">
                <ShieldCheck className="h-5 w-5 text-electric" />
                <Sparkles className="h-5 w-5 text-electric" />
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-extrabold">Ready to reach decision-makers?</h2>
              <p className="mt-4 text-white/75 max-w-lg">
                Launch sponsored content, lead-generation campaigns and research sponsorships with Lead ITES.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button asChild className="btn-electric rounded-full px-6 h-11" data-testid="home-partner-cta">
                <Link to="/partnerships">Partner With Us</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-6 h-11 bg-transparent border-white/30 text-white hover:bg-white hover:text-navy" data-testid="home-getstarted-cta">
                <Link to="/lead-generation">Get Started</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
